export const SURFACE_MODES = Object.freeze({
  constant: "constant",
  pressure: "pressure",
});

export const APP_BUILD_ID = "webxr-vr49-20260706b";

export const AVAILABLE_VELOCITY_TEXTURE_LEVELS = Object.freeze(
  Array.from({ length: 70 }, (_, index) => index)
);

export const DEFAULT_VELOCITY_TEXTURE_LEVEL = AVAILABLE_VELOCITY_TEXTURE_LEVELS[40] ?? AVAILABLE_VELOCITY_TEXTURE_LEVELS[0];
export const DEFAULT_SURFACE_MODE = SURFACE_MODES.constant;

export const WIND_LAYER_BLEND_MODES = Object.freeze({
  single: "single",
  adjacent: "adjacent",
  wide: "wide",
  seven: "seven",
});

export const DEFAULT_WIND_LAYER_BLEND_MODE = WIND_LAYER_BLEND_MODES.single;

export const PARTICLE_PLACEMENT = Object.freeze({
  visibleCap: "visible-cap",
  fullSphere: "full-sphere",
});

export const WIND_LIMITS = Object.freeze({
  minLon: -224.53910722953327,
  maxLon: 95.10090082700401,
  minLat: -33.77539488741429,
  maxLat: 38.422694869835304,
  maxMagnitude: 224.5472600359052,
});

export const WIND_MAGNITUDE_RANGE = Object.freeze({
  min: 0,
  max: WIND_LIMITS.maxMagnitude,
});

export const PRESSURE_LIMITS = Object.freeze({
  min: 750.0269542911284,
  max: 282248.79956396087,
});

export const APP_BASE_PATH = (process.env.PUBLIC_URL || "").replace(/\/$/, "");

export function withBasePath(path) {
  const normalisedPath = path.startsWith("/") ? path : `/${path}`;
  return `${APP_BASE_PATH}${normalisedPath}`;
}

export const DATASET_METADATA_PATH = withBasePath("/textures/generated-hdf5/wind-metadata.json");

export const WIND_LEGEND_SCALE = Object.freeze([
  { color: "#30123b", position: 0.0 },
  { color: "#4145ab", position: 0.14 },
  { color: "#2a9df4", position: 0.28 },
  { color: "#1ae4b6", position: 0.43 },
  { color: "#a1fc3d", position: 0.58 },
  { color: "#faba39", position: 0.72 },
  { color: "#f36315", position: 0.86 },
  { color: "#7a0403", position: 1.0 },
]);

export const WIND_LEGEND_STOPS = Object.freeze(
  buildWindLegendStops(WIND_MAGNITUDE_RANGE)
);

export const PRESSURE_LEGEND_SCALE = Object.freeze([
  { color: "#140d0a", position: 0.0 },
  { color: "#3a2418", position: 0.18 },
  { color: "#6a4124", position: 0.42 },
  { color: "#996536", position: 0.66 },
  { color: "#bf8d52", position: 0.84 },
  { color: "#d9b77d", position: 1.0 },
]);

export const PRESSURE_LEGEND_STOPS = Object.freeze(
  buildPressureLegendStops(PRESSURE_LIMITS.min, PRESSURE_LIMITS.max)
);

export const VISUAL_SETTINGS = Object.freeze({
  afterimageDamp: 0.99,
  afterimageInteractionDamp: 0.78,
  afterimageRecoveryMs: 180,
  motionScale: 0.009,
  particleResolution: 100,
  planetRadius: 1,
  maxDeltaSeconds: 0.02,
  maxPixelRatio: 2,
  respawnResetChance: 0.995,
  interactiveResetChance: 0.4,
  respawnThrottleMs: 140,
  webxrQuest: {
    maxPixelRatio: 1.25,
    framebufferScaleFactor: 0.85,
    particleResolution: 64,
    defaultPlanetScale: 0.72,
    minPlanetScale: 0.48,
    maxPlanetScale: 1.05,
  },
});

export function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export function resolveVelocityTextureLevel(level) {
  if (AVAILABLE_VELOCITY_TEXTURE_LEVELS.includes(level)) {
    return level;
  }

  return AVAILABLE_VELOCITY_TEXTURE_LEVELS.reduce((closest, candidate) => {
    const closestDistance = Math.abs(closest - level);
    const candidateDistance = Math.abs(candidate - level);
    return candidateDistance < closestDistance ? candidate : closest;
  }, DEFAULT_VELOCITY_TEXTURE_LEVEL);
}

export function getWindLayerBlendRadius(mode = DEFAULT_WIND_LAYER_BLEND_MODE) {
  if (mode === WIND_LAYER_BLEND_MODES.adjacent) {
    return 1;
  }

  if (mode === WIND_LAYER_BLEND_MODES.wide) {
    return 2;
  }

  if (mode === WIND_LAYER_BLEND_MODES.seven) {
    return 3;
  }

  return 0;
}

export function getWindLayerBlendLevels(level, mode = DEFAULT_WIND_LAYER_BLEND_MODE) {
  const resolvedLevel = resolveVelocityTextureLevel(level);
  const levelIndex = AVAILABLE_VELOCITY_TEXTURE_LEVELS.indexOf(resolvedLevel);
  const radius = getWindLayerBlendRadius(mode);

  if (radius <= 0 || levelIndex < 0) {
    return [resolvedLevel];
  }

  const levels = [];
  const start = Math.max(0, levelIndex - radius);
  const end = Math.min(AVAILABLE_VELOCITY_TEXTURE_LEVELS.length - 1, levelIndex + radius);

  for (let index = start; index <= end; index += 1) {
    levels.push(AVAILABLE_VELOCITY_TEXTURE_LEVELS[index]);
  }

  return levels;
}

export function getWindLayerBlendCount(level, mode = DEFAULT_WIND_LAYER_BLEND_MODE) {
  return getWindLayerBlendLevels(level, mode).length;
}

export function getNextWindLayerBlendMode(currentMode = DEFAULT_WIND_LAYER_BLEND_MODE) {
  const modes = [
    WIND_LAYER_BLEND_MODES.single,
    WIND_LAYER_BLEND_MODES.adjacent,
    WIND_LAYER_BLEND_MODES.wide,
    WIND_LAYER_BLEND_MODES.seven,
  ];
  const currentIndex = modes.indexOf(currentMode);

  return modes[(currentIndex + 1) % modes.length] ?? DEFAULT_WIND_LAYER_BLEND_MODE;
}

export function formatWindLayerBlendLabel(mode = DEFAULT_WIND_LAYER_BLEND_MODE) {
  if (mode === WIND_LAYER_BLEND_MODES.adjacent) {
    return "3 stacked layers";
  }

  if (mode === WIND_LAYER_BLEND_MODES.wide) {
    return "5 stacked layers";
  }

  if (mode === WIND_LAYER_BLEND_MODES.seven) {
    return "7 stacked layers";
  }

  return "single layer";
}

export function formatWindLayerShortLabel(mode = DEFAULT_WIND_LAYER_BLEND_MODE) {
  if (mode === WIND_LAYER_BLEND_MODES.adjacent) {
    return "3 layers";
  }

  if (mode === WIND_LAYER_BLEND_MODES.wide) {
    return "5 layers";
  }

  if (mode === WIND_LAYER_BLEND_MODES.seven) {
    return "7 layers";
  }

  return "1 layer";
}

export function formatWindLayerBlendButtonLabel(mode = DEFAULT_WIND_LAYER_BLEND_MODE) {
  if (mode === WIND_LAYER_BLEND_MODES.adjacent) {
    return "Layers: 3";
  }

  if (mode === WIND_LAYER_BLEND_MODES.wide) {
    return "Layers: 5";
  }

  if (mode === WIND_LAYER_BLEND_MODES.seven) {
    return "Layers: 7";
  }

  return "Layers: 1";
}

export function getVelocityTexturePath(level) {
  return withBasePath(`/textures/generated-hdf5/velocityTexture${resolveVelocityTextureLevel(level)}.png`);
}

export function getPressureTexturePath(level) {
  return withBasePath(`/textures/generated-hdf5/pressureTexture${resolveVelocityTextureLevel(level)}.png`);
}

export function hexToRgb(hex) {
  const normalised = hex.replace("#", "");
  const value = Number.parseInt(normalised, 16);

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

export function rgbToHex(r, g, b) {
  const toHex = (channel) => channel.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function decodeWindVector(sample) {
  const lon = (sample.r * (WIND_LIMITS.maxLon - WIND_LIMITS.minLon)) + WIND_LIMITS.minLon;
  const lat = (sample.g * (WIND_LIMITS.maxLat - WIND_LIMITS.minLat)) + WIND_LIMITS.minLat;
  const magnitude = Math.sqrt((lon ** 2) + (lat ** 2));

  return { lon, lat, magnitude };
}

export function normaliseWindMagnitudeRange(rangeOrMax = WIND_MAGNITUDE_RANGE) {
  if (typeof rangeOrMax === "number") {
    return {
      min: 0,
      max: Number.isFinite(rangeOrMax) && rangeOrMax > 0
        ? rangeOrMax
        : WIND_LIMITS.maxMagnitude,
    };
  }

  const min = Number.isFinite(rangeOrMax?.min) && rangeOrMax.min >= 0
    ? rangeOrMax.min
    : 0;
  const max = Number.isFinite(rangeOrMax?.max) && rangeOrMax.max > min
    ? rangeOrMax.max
    : Number.isFinite(rangeOrMax?.maxMagnitude) && rangeOrMax.maxMagnitude > min
      ? rangeOrMax.maxMagnitude
      : WIND_LIMITS.maxMagnitude;

  return {
    min,
    max: Math.max(max, min + 1e-6),
  };
}

export function getWindMagnitudeRatio(magnitude, rangeOrMax = WIND_MAGNITUDE_RANGE) {
  const range = normaliseWindMagnitudeRange(rangeOrMax);
  return clamp((magnitude - range.min) / Math.max(range.max - range.min, 1e-6), 0, 1);
}

export function buildWindLegendStops(rangeOrMax = WIND_MAGNITUDE_RANGE) {
  const range = normaliseWindMagnitudeRange(rangeOrMax);
  const magnitudeSpan = range.max - range.min;

  return WIND_LEGEND_SCALE.map((stop) => ({
    label: formatWindLegendValue(range.min + (stop.position * magnitudeSpan)),
    color: stop.color,
    position: stop.position,
  }));
}

export function buildPressureLegendStops(
  minPressure = PRESSURE_LIMITS.min,
  maxPressure = PRESSURE_LIMITS.max
) {
  const pressureRange = Math.max(maxPressure - minPressure, 1e-6);

  return PRESSURE_LEGEND_SCALE.map((stop) => ({
    label: formatPressureLegendValue(minPressure + (stop.position * pressureRange)),
    color: stop.color,
    position: stop.position,
  }));
}

export function getWindMagnitudeLimitForLevel(metadata, level) {
  return getWindMagnitudeRangeForLevel(metadata, level).max;
}

export function getWindMagnitudeRangeForLevel(metadata, level) {
  const levelLimits = metadata?.levelLimits;
  const entry = levelLimits?.[level] ?? levelLimits?.[String(level)];

  if (Number.isFinite(entry?.maxMagnitude) && entry.maxMagnitude > 0) {
    return normaliseWindMagnitudeRange({
      min: Number.isFinite(entry?.minMagnitude) && entry.minMagnitude >= 0
        ? entry.minMagnitude
        : 0,
      max: entry.maxMagnitude,
    });
  }

  return WIND_MAGNITUDE_RANGE;
}

export function getPressureLimitsForLevel(metadata, level) {
  const pressureLevelLimits = metadata?.pressureLevelLimits;
  const entry = pressureLevelLimits?.[level] ?? pressureLevelLimits?.[String(level)];

  if (
    Number.isFinite(entry?.min) &&
    Number.isFinite(entry?.max) &&
    entry.max > entry.min
  ) {
    return {
      min: entry.min,
      max: entry.max,
    };
  }

  return PRESSURE_LIMITS;
}

export function getWindColor(magnitude, rangeOrMax = WIND_MAGNITUDE_RANGE) {
  const position = getWindMagnitudeRatio(magnitude, rangeOrMax);

  let start = WIND_LEGEND_SCALE[0];
  let end = WIND_LEGEND_SCALE[WIND_LEGEND_SCALE.length - 1];

  for (let index = 0; index < WIND_LEGEND_SCALE.length - 1; index += 1) {
    const current = WIND_LEGEND_SCALE[index];
    const next = WIND_LEGEND_SCALE[index + 1];

    if (position >= current.position && position <= next.position) {
      start = current;
      end = next;
      break;
    }
  }

  const startRgb = hexToRgb(start.color);
  const endRgb = hexToRgb(end.color);
  const range = Math.max(end.position - start.position, 0.0001);
  const localPosition = (position - start.position) / range;

  const r = Math.round(startRgb.r + ((endRgb.r - startRgb.r) * localPosition));
  const g = Math.round(startRgb.g + ((endRgb.g - startRgb.g) * localPosition));
  const b = Math.round(startRgb.b + ((endRgb.b - startRgb.b) * localPosition));

  return rgbToHex(r, g, b);
}

function formatWindLegendValue(value) {
  if (value >= 100) {
    return `${Math.round(value)}`;
  }

  if (value >= 10) {
    return `${Math.round(value)}`;
  }

  return `${Math.round(value * 10) / 10}`;
}

function formatPressureLegendValue(valueInPa) {
  const valueInKPa = valueInPa / 1000;

  if (valueInKPa >= 100) {
    return `${Math.round(valueInKPa)}`;
  }

  if (valueInKPa >= 10) {
    return `${Math.round(valueInKPa * 10) / 10}`;
  }

  return `${Math.round(valueInKPa * 100) / 100}`;
}
